import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import SimpleAutoComplete from "../auto-complete/autocomplete";
import PlaceholderSelector from "./PlaceholderSelector";
import { PlaceType } from "./AddCommonPlaceholder";
import {
  getAutomationPlaceholders,
  lookupForAutomation,
  renderForAutomation,
} from "../../services/emailtemplate.service";
import { findUnknownPlaceholders } from "../../utils/emailPlaceholderUtils";
import useEmailAssetDisplay from "./useEmailAssetDisplay";

// Editores pesados (Jodit ~870KB / Monaco via CDN) so carregam quando a action de e-mail aparece.
const RichTextEditor = React.lazy(() => import("../rich-text-editor/RichTextEditor"));
const CodeEditor = React.lazy(() => import("../code-editor/CodeEditor"));

// Actions de e-mail da automacao (10 agente, 11 solicitante, 22 observador, 23 externas).
//
// Dois modos:
//  - "custom": e-mail livre (fragmento HTML) — o Worker embrulha no layout padrao "Prezado(a)".
//  - "template": usa um Template de Email salvo. Assunto + documento HTML completo sao COPIADOS pra action
//    (snapshot — mudancas futuras no template nao afetam a automacao) e o Worker envia exatamente como esta.
// Edicao VISUAL por padrao (Jodit: o proprio e-mail renderizado e editavel). HTML so no switch avancado (Monaco).
//
// Variaveis: GET /automation/email-placeholders?target= (mesma fonte da validacao do backend). Variavel fora
// do conjunto do target e sinalizada aqui e bloqueia o save (onUnknownPlaceholdersChange informa a pagina).

const ALL_GROUP_TYPES = [
  PlaceType.Chamado,
  PlaceType.Portal,
  PlaceType.Empresa,
  PlaceType.Solicitante,
  PlaceType.Chat,
];

const EDITOR_HEIGHT = 560;
const MOBILE_WIDTH = 390;

const toggleSx = {
  "& .MuiToggleButton-root": {
    textTransform: "none",
    px: 2,
    borderRadius: "10px !important",
    border: "1px solid #e0e0e0 !important",
    mr: 1,
  },
  "& .Mui-selected": {
    backgroundColor: "#eef7e3 !important",
    color: "#5b9b1e",
    borderColor: "#8BC34A !important",
  },
};

const sectionTitleSx = { fontSize: 14, fontWeight: 600, color: "#444", mb: 0.75 };

export default function AutomationEmailConfig({
  targetEntity = "called",
  subjectValue,
  bodyValue,
  templateId,
  onChangeSubject,
  onChangeBody,
  onChangeTemplateId,
  onUnknownPlaceholdersChange,
}) {
  const intl = useIntl();
  const isChat = (targetEntity || "").toLowerCase() === "chat";

  const [mode, setMode] = useState(templateId ? "template" : "custom");
  const [subject, setSubject] = useState(subjectValue ?? "");
  const [body, setBody] = useState(bodyValue ?? "");
  const [selectedTemplateId, setSelectedTemplateId] = useState(templateId || null);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingRender, setLoadingRender] = useState(false);
  const [renderError, setRenderError] = useState("");

  const [placeholders, setPlaceholders] = useState(null); // null = ainda nao carregou / falhou
  const [advanced, setAdvanced] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);

  const visualRef = useRef(null);
  const codeRef = useRef(null);
  const subjectInputRef = useRef(null);

  // Imagens do template: marcador mexx-email-asset://{id} ↔ URL temporaria so na exibicao do editor visual.
  const assetDisplay = useEmailAssetDisplay(body, () => {
    if (visualRef.current) visualRef.current.refreshDisplay();
  });

  // Variaveis permitidas do target + templates ativos (uma vez por target).
  useEffect(() => {
    let alive = true;
    getAutomationPlaceholders(isChat ? "chat" : "called").then((r) => {
      if (alive && r.success) setPlaceholders(r.data);
    });
    lookupForAutomation().then((r) => {
      if (!alive) return;
      setTemplates((r.data || []).map((t) => ({ id: t.id, name: t.name, subject: t.subject })));
      setLoadingTemplates(false);
    });
    return () => {
      alive = false;
    };
  }, [isChat]);

  const allowedNames = useMemo(
    () => (placeholders ? placeholders.map((p) => p.name) : null),
    [placeholders]
  );

  const unknownPlaceholders = useMemo(() => {
    if (!allowedNames) return [];
    return findUnknownPlaceholders([subject, body], allowedNames, isChat ? "chat" : "called");
  }, [subject, body, allowedNames, isChat]);

  useEffect(() => {
    if (onUnknownPlaceholdersChange) onUnknownPlaceholdersChange(unknownPlaceholders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unknownPlaceholders.join("|")]);

  const templateOptions = useMemo(() => {
    if (!selectedTemplateId || templates.some((t) => t.id === selectedTemplateId)) return templates;
    // Template vinculado que foi inativado/excluido depois: mantem visivel (o snapshot segue valido).
    return [
      ...templates,
      {
        id: selectedTemplateId,
        name: intl.formatMessage(
          { id: "automation.email.template.unavailable" },
          { id: selectedTemplateId }
        ),
      },
    ];
  }, [templates, selectedTemplateId, intl]);

  function updateSubject(value) {
    setSubject(value);
    if (onChangeSubject) onChangeSubject(value);
  }

  function updateBody(value) {
    setBody(value);
    if (onChangeBody) onChangeBody(value);
  }

  function updateTemplateId(id) {
    setSelectedTemplateId(id);
    if (onChangeTemplateId) onChangeTemplateId(id);
  }

  function loadTemplate(id) {
    setRenderError("");
    if (!id) {
      updateTemplateId(null);
      return;
    }
    setLoadingRender(true);
    renderForAutomation(id).then((r) => {
      setLoadingRender(false);
      if (!r.success || !r.data) {
        setRenderError(r.message || intl.formatMessage({ id: "automation.email.template.loadError" }));
        return;
      }
      updateTemplateId(id);
      updateSubject(r.data.subject ?? "");
      updateBody(r.data.html ?? "");
    });
  }

  function handleModeChange(event, value) {
    if (!value || value === mode) return;
    setMode(value);
    setRenderError("");
    setAdvanced(false);
    if (value === "custom" && selectedTemplateId) {
      // Documento completo do template nao vira e-mail livre (o Worker embrulharia de novo) → recomeca o corpo.
      updateTemplateId(null);
      updateBody("");
    }
  }

  function insertIntoBody(placeholder) {
    if (!placeholder) return;
    const target = advanced ? codeRef.current : visualRef.current;
    if (target) target.insertText(placeholder);
    else updateBody(`${body || ""}${placeholder}`);
  }

  function insertIntoSubject(placeholder) {
    if (!placeholder) return;
    const el = subjectInputRef.current;
    const current = subject || "";
    const start = el ? el.selectionStart ?? current.length : current.length;
    const end = el ? el.selectionEnd ?? start : start;
    updateSubject(current.slice(0, start) + placeholder + current.slice(end));
    setTimeout(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 50);
  }

  function confirmRestore() {
    setConfirmRestoreOpen(false);
    loadTemplate(selectedTemplateId);
  }

  const placeholderProps = placeholders ? { types: ALL_GROUP_TYPES, placeholders } : {};
  const isTemplate = mode === "template";
  const hasTemplateContent = isTemplate && !!selectedTemplateId;
  const showEditor = !isTemplate || hasTemplateContent;
  const editorFallback = <Skeleton variant="rectangular" height={EDITOR_HEIGHT} animation="wave" sx={{ borderRadius: "12px" }} />;

  return (
    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "12px", p: 2.5, backgroundColor: "#fff" }}>
      <Typography sx={sectionTitleSx}>
        {intl.formatMessage({ id: "automation.email.mode.question" })}
      </Typography>
      <ToggleButtonGroup exclusive size="small" value={mode} onChange={handleModeChange} sx={{ mb: 2, ...toggleSx }}>
        <ToggleButton value="custom">{intl.formatMessage({ id: "automation.email.mode.compose" })}</ToggleButton>
        <ToggleButton value="template">{intl.formatMessage({ id: "automation.email.mode.template" })}</ToggleButton>
      </ToggleButtonGroup>

      {isTemplate && (
        <Box sx={{ mb: 2 }}>
          <SimpleAutoComplete
            label={intl.formatMessage({ id: "automation.email.template.pick" })}
            options={templateOptions}
            size="small"
            stateName="automationEmailTemplate"
            loading={loadingTemplates || loadingRender}
            selected={templateOptions.find((t) => t.id === selectedTemplateId) || { id: 0, name: "" }}
            changeSelect={(n, v) => loadTemplate(v && v.id ? v.id : null)}
          />
          {!loadingTemplates && templates.length === 0 && (
            <Typography sx={{ fontSize: 13, color: "#888", mt: 1 }}>
              {intl.formatMessage({ id: "automation.email.template.empty" })}
            </Typography>
          )}
          {!!renderError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {renderError}
            </Alert>
          )}
          {hasTemplateContent && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {intl.formatMessage({ id: "automation.email.template.snapshotInfo" })}
            </Alert>
          )}
          {!selectedTemplateId && !loadingRender && templates.length > 0 && (
            <Typography sx={{ fontSize: 13, color: "#888", mt: 1 }}>
              {intl.formatMessage({ id: "automation.email.template.pickHint" })}
            </Typography>
          )}
        </Box>
      )}

      {showEditor && (
        <>
          <TextField
            fullWidth
            size="small"
            label={intl.formatMessage({ id: "automation.email.subject" })}
            placeholder={intl.formatMessage({ id: "automation.email.subject.placeholder" })}
            value={subject}
            onChange={(e) => updateSubject(e.target.value)}
            inputRef={subjectInputRef}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PlaceholderSelector onlyButton onSelect={insertIntoSubject} {...placeholderProps} />
                </InputAdornment>
              ),
            }}
          />

          {/* Barra do editor: variaveis · desktop/celular · HTML avancado · menu */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ ...sectionTitleSx, mb: 0 }}>
                {intl.formatMessage({ id: "automation.email.body" })}
              </Typography>
              <Tooltip title={intl.formatMessage({ id: "automation.email.insertVariable" })}>
                <Box>
                  <PlaceholderSelector onlyButton onSelect={insertIntoBody} {...placeholderProps} />
                </Box>
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {!advanced && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={device}
                  onChange={(e, v) => v && setDevice(v)}
                >
                  <ToggleButton value="desktop" aria-label="desktop">
                    <Tooltip title={intl.formatMessage({ id: "automation.email.device.desktop" })}>
                      <DesktopWindowsOutlinedIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="mobile" aria-label="mobile">
                    <Tooltip title={intl.formatMessage({ id: "automation.email.device.mobile" })}>
                      <PhoneIphoneOutlinedIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              <FormControlLabel
                sx={{ m: 0, whiteSpace: "nowrap" }}
                control={<Switch size="small" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} />}
                label={
                  <Typography sx={{ fontSize: 13, color: "#555" }}>
                    {intl.formatMessage({ id: "automation.email.advancedHtml" })}
                  </Typography>
                }
              />
              {hasTemplateContent && (
                <>
                  <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                    <MenuItem
                      disabled={loadingRender}
                      onClick={() => {
                        setMenuAnchor(null);
                        setConfirmRestoreOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <RefreshIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ sx: { whiteSpace: "nowrap", fontSize: 14 } }}>
                        {intl.formatMessage({ id: "automation.email.template.restore" })}
                      </ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Box>

          <Suspense fallback={editorFallback}>
            {advanced ? (
              <CodeEditor ref={codeRef} language="html" value={body} onChange={updateBody} height={EDITOR_HEIGHT} />
            ) : (
              <Box
                sx={{
                  width: device === "mobile" ? MOBILE_WIDTH : "100%",
                  maxWidth: "100%",
                  mx: "auto",
                  transition: "width .2s ease",
                  "& .jodit-container": { borderRadius: "12px", borderColor: "#e0e0e0" },
                }}
              >
                <RichTextEditor
                  key={`${mode}-${device}`}
                  ref={visualRef}
                  value={body}
                  onChange={updateBody}
                  fullDocument={isTemplate}
                  height={EDITOR_HEIGHT}
                  locale={intl.locale}
                  toDisplay={assetDisplay.toDisplay}
                  fromDisplay={assetDisplay.fromDisplay}
                />
              </Box>
            )}
          </Suspense>

          {unknownPlaceholders.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>{intl.formatMessage({ id: "automation.email.unknownVars.title" })}</AlertTitle>
              <Typography sx={{ fontSize: 13, mb: 1 }}>
                {intl.formatMessage({
                  id: isChat ? "automation.email.unknownVars.chat" : "automation.email.unknownVars.called",
                })}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {unknownPlaceholders.map((item) => (
                  <Chip key={item} label={item} size="small" color="warning" variant="outlined" />
                ))}
              </Box>
            </Alert>
          )}
        </>
      )}

      <Dialog open={confirmRestoreOpen} onClose={() => setConfirmRestoreOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: "automation.email.template.restore" })}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#555" }}>
            {intl.formatMessage({ id: "automation.email.template.restoreConfirm" })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestoreOpen(false)} sx={{ textTransform: "none" }}>
            {intl.formatMessage({ id: "automation.email.cancel" })}
          </Button>
          <Button variant="contained" color="warning" onClick={confirmRestore} sx={{ textTransform: "none" }}>
            {intl.formatMessage({ id: "automation.email.template.restore" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
